import type { JSX } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import type { DocType } from "../../domain/entities/doc-type";
import { useDeleteDocType } from "../hooks/use-delete-doc-type";

export interface DeleteDocTypeDialogProps {
  docType: DocType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: (id: string) => void;
}

export function DeleteDocTypeDialog({
  docType,
  open,
  onOpenChange,
  onDeleted,
}: DeleteDocTypeDialogProps): JSX.Element {
  const deleteDocType = useDeleteDocType();

  const handleDelete = (): void => {
    deleteDocType.mutate(docType.id, {
      onSuccess: () => {
        onOpenChange(false);
        onDeleted(docType.id);
      },
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete document type?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete “{docType.displayName}”. This action
            cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteDocType.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={deleteDocType.isPending}
            onClick={handleDelete}
          >
            {deleteDocType.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
